import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import { Component, computed, effect, forwardRef, inject, input, linkedSignal, output, signal, untracked } from '@angular/core';
import { rxResource, takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FormlyFieldConfig, FormlyForm, FormlyFormOptions } from '@ngx-formly/core';
import { cloneDeep, uniq } from 'lodash-es';
import { DateTime } from 'luxon';
import { catchError, of, switchMap } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { LoadingComponent } from '../../component/loading/loading.component';
import { RefComponent } from '../../component/ref/ref.component';
import { RelativeDateComponent } from '../../component/relative-date/relative-date.component';
import { ValueFilterComponent } from '../../component/value-filter/value-filter.component';
import { Ext } from '../../model/ext';
import { Ref } from '../../model/ref';
import { getMailbox } from '../../mods/mailbox';
import { AdminService } from '../../service/admin.service';
import { RefService } from '../../service/api/ref.service';
import { Store } from '../../store/store';
import { controlValue } from '../../util/form';
import { TAG_REGEX } from '../../util/format';
import {
  convertFilter,
  convertSort,
  defaultDesc,
  FilterItem,
  negatable,
  parseValueFilter,
  relativeDate,
  relativeDateLabel,
  relativeDatePreset,
  switchFilter,
  toggle,
  UrlFilter
} from '../../util/query';
import { hasPrefix } from '../../util/tag';
import { linksForm } from '../links/links.component';
import { themesForm, ThemesFormComponent } from '../themes/themes.component';

@Component({
  selector: 'app-ext-form',
  templateUrl: './ext.component.html',
  styleUrls: ['./ext.component.scss'],
  host: { 'class': 'nested-form' },
  imports: [
    forwardRef(() => RefComponent),
    ReactiveFormsModule,
    FormlyForm,
    CdkDropListGroup,
    RouterLink,
    ThemesFormComponent,
    LoadingComponent,
    ValueFilterComponent,
    RelativeDateComponent,
  ],
})
export class ExtFormComponent  {
  admin = inject(AdminService);
  store = inject(Store);
  private refs = inject(RefService);

  private readonly rootControlState = controlValue(() => this.group());

  private readonly controlState0 = controlValue(() => this.group().get('tag')!);
  private readonly controlState1 = controlValue(() => this.themes());

  readonly allSorts = signal(this.admin.refSorts().map(convertSort));
  readonly allFilters = signal<FilterItem[]>([
    { filter: `modified/before/${DateTime.now().toISO()}`, label: $localize`🕓️ modified before` },
    { filter: `modified/after/${DateTime.now().toISO()}`, label: $localize`🕓️ modified after` },
    { filter: `response/before/${DateTime.now().toISO()}`, label: $localize`🧵️ response before` },
    { filter: `response/after/${DateTime.now().toISO()}`, label: $localize`🧵️ response after` },
    { filter: `published/before/${DateTime.now().toISO()}`, label: $localize`📅️ published before` },
    { filter: `published/after/${DateTime.now().toISO()}`, label: $localize`📅️ published after` },
    { filter: `created/before/${DateTime.now().toISO()}`, label: $localize`✨️ created before` },
    { filter: `created/after/${DateTime.now().toISO()}`, label: $localize`✨️ created after` },
    ...this.admin.filters().map(convertFilter),
  ]);

  readonly group = input.required<UntypedFormGroup>();
  /**
   * The Ext the form was filled from. Formly fields are built from its config.
   */
  readonly ext = input<Ext | undefined>(undefined);
  readonly showClear = input(false);
  readonly clear = output<void>();

  id = 'ext-' + uuid();
  readonly form = linkedSignal(() => {
    const tag = this.ext()?.tag;
    this.group();
    return tag === undefined ? undefined : cloneDeep(this.admin.getTemplateForm(tag));
  });
  readonly advancedForm = linkedSignal(() => {
    const tag = this.ext()?.tag;
    this.group();
    return tag === undefined ? undefined : cloneDeep(this.admin.getTemplateAdvancedForm(tag));
  });
  /**
   * Formly model for the template fields. Formly writes the model into the
   * form, so values already in the form group (a hidden draft) take precedence
   * over the Ext.
   */
  readonly model = computed(() => {
    const group = this.group();
    const config = this.ext()?.config;
    return untracked(() => ({
      ...cloneDeep(config),
      ...cloneDeep(group.get('config')?.getRawValue()),
    }));
  });
  private readonly defaultsResource = rxResource({
    params: () => this.ext()?.config?.defaults ? this.ext()!.tag : undefined,
    stream: ({ params: tag }) => this.refs.getCurrent('tag:/' + tag).pipe(
      catchError(() => of(undefined)),
    ),
  });
  readonly loadingDefaults = linkedSignal(() => this.defaultsResource.isLoading());
  readonly defaults = linkedSignal<Ref | undefined>(() => this.defaultsResource.hasValue() ? this.defaultsResource.value() : undefined);

  options: FormlyFormOptions = {
    formState: {
      admin: this.admin,
      config: { }
    }
  };

  constructor() {
    // Sync the Ext config into the formly form state
    effect(() => {
      this.options.formState.config = this.ext()?.config;
    });
    toObservable(this.config).pipe(
      switchMap(config => config.valueChanges),
      takeUntilDestroyed(),
    ).subscribe(value => {
      if (value.defaults) {
        if (!this.defaults()) this.createDefaults();
      } else {
        this.defaults.set(undefined);
        this.loadingDefaults.set(false);
      }
    });
  }

  readonly user = computed(() => {
    this.rootControlState();
    this.controlState0();
    if (!this.admin.getTemplate('user')) return false;
    return hasPrefix(this.group().get('tag')!.value, 'user');
  });

  readonly config = computed(() => {
    this.rootControlState();
    return this.group().get('config') as UntypedFormGroup;
  });

  readonly inbox = computed(() => {
    this.rootControlState();
    this.controlState0();
    if (!this.admin.getPlugin('plugin/inbox')) return null;
    return getMailbox(this.group().get('tag')!.value, this.store.account.origin());
  });

  readonly modmail = computed(() => {
    this.rootControlState();
    return this.config().get('modmail') as FormControl<boolean>;
  });

  readonly defaultSort = computed(() => {
    this.rootControlState();
    return this.config().get('defaultSort') as FormControl<string[]>;
  });

  readonly defaultFilter = computed(() => {
    this.rootControlState();
    return this.config().get('defaultFilter') as FormControl<UrlFilter[]>;
  });
  protected readonly defaultSortValue = controlValue(this.defaultSort);
  protected readonly defaultFilterValue = controlValue(this.defaultFilter);

  addSort(value: string, select: HTMLSelectElement) {
    if (!value) return;
    this.defaultSort().setValue([
      ...this.defaultSort().value || [],
      value + ',' + (defaultDesc(value) ? 'DESC' : 'ASC'),
    ]);
    select.selectedIndex = 0;
  }

  sortCol(sort: string) {
    if (!sort.includes(',')) return sort;
    return sort.split(',')[0];
  }

  sortDir(sort: string) {
    if (!sort.includes(',')) return defaultDesc(sort) ? 'DESC' : 'ASC';
    return sort.split(',')[1].toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
  }

  setSortCol(index: number, value: string) {
    const sorts = [...this.defaultSort().value];
    sorts[index] = value + ',' + this.sortDir(value);
    this.defaultSort().setValue(sorts);
  }

  setSortDir(index: number, value: string) {
    const sorts = [...this.defaultSort().value];
    sorts[index] = this.sortCol(sorts[index]) + ',' + value;
    this.defaultSort().setValue(sorts);
  }

  removeSort(index: number) {
    const sorts = [...this.defaultSort().value];
    sorts.splice(index, 1);
    this.defaultSort().setValue(sorts);
  }

  addFilter(value: UrlFilter, select: HTMLSelectElement) {
    if (!value) return;
    this.defaultFilter().setValue([...this.defaultFilter().value || [], value]);
    select.selectedIndex = 0;
  }

  setFilter(index: number, value: UrlFilter) {
    const filters = [...this.defaultFilter().value];
    filters[index] = value;
    this.defaultFilter().setValue(filters);
  }

  removeFilter(index: number) {
    const filters = [...this.defaultFilter().value];
    filters.splice(index, 1);
    this.defaultFilter().setValue(filters);
  }

  toggleFilter(index: number) {
    const filters = [...this.defaultFilter().value];
    filters[index] = toggle(filters[index])!;
    this.defaultFilter().setValue(filters);
  }

  setFilterDate(index: number, filter: UrlFilter, date: string) {
    if (!date) return;
    this.setFilter(index, (filter.substring(0, filter.lastIndexOf('/') + 1) + DateTime.fromISO(date).toISO()) as UrlFilter);
  }

  filterOption(filter: UrlFilter) {
    const v = parseValueFilter(filter);
    if (!v && !this.filterIsDate(filter)) return filter;
    const prefix = v ? `${v.column}/${v.dir}/` : filter.substring(0, filter.lastIndexOf('/') + 1);
    return this.allFilters().find(option => option.filter.startsWith(prefix))?.filter || filter;
  }

  filterOptionLabel(filter: UrlFilter) {
    const option = negatable(filter) && this.allFilters().find(item => item.filter === toggle(filter));
    return option ? this.store.account.querySymbol('!') + (option.label || option.filter) : filter;
  }

  hasFilterOption(filter: UrlFilter) {
    const selected = this.filterOption(filter);
    return this.allFilters().some(option => option.filter === selected);
  }

  readonly parseValueFilter = parseValueFilter;
  readonly switchFilter = switchFilter;

  setFilterValue(index: number, filter: UrlFilter, value: string) {
    const v = parseValueFilter(filter);
    if (v) this.setFilter(index, `${v.column}/${v.dir}/${value}` as UrlFilter);
  }

  filterIsDate(filter: UrlFilter) {
    return /^(modified|response|published|created)\/(before|after)\//.test(filter);
  }

  filterDateValue(filter: UrlFilter) {
    return filter.substring(filter.lastIndexOf('/') + 1);
  }

  filterDateLabel(filter: UrlFilter) {
    return relativeDateLabel(this.filterDateValue(filter));
  }

  filterSpecialDate(filter: UrlFilter) {
    return relativeDate(this.filterDateValue(filter));
  }

  filterDatePreset(filter: UrlFilter) {
    return relativeDatePreset(this.filterDateValue(filter));
  }

  setFilterDatePreset(index: number, filter: UrlFilter, preset: string) {
    this.setFilter(index, (filter.substring(0, filter.lastIndexOf('/') + 1) + preset) as UrlFilter);
  }

  filterDate(filter: UrlFilter) {
    const date = DateTime.fromISO(filter.substring(filter.lastIndexOf('/') + 1));
    return date.isValid ? date.toFormat("yyyy-MM-dd'T'T") : '';
  }

  readonly themes = computed(() => {
    this.rootControlState();
    return this.config().get('themes') as UntypedFormGroup;
  });

  readonly userTheme = computed(() => {
    this.rootControlState();
    return this.config().get('userTheme') as UntypedFormGroup;
  });

  readonly themeValues = computed(() => {
    this.rootControlState();
    this.controlState1();
    return uniq([...Object.keys(this.themes()?.value || {}), ...this.admin.themes().flatMap(p => Object.keys(p.config?.themes || {}))]);
  });

  readonly userThemeValues = computed(() => {
    this.rootControlState();
    this.controlState1();
    return uniq([...Object.keys(this.themes()?.value || {}), ...this.admin.themes().flatMap(p => Object.keys(p.config?.themes || {}))]);
  });

  readonly pinned = computed(() => {
    this.rootControlState();
    return this.config().get('pinned') as UntypedFormControl;
  });

  negatable(filter: string) {
    return negatable(filter);
  }

  createDefaults() {
    this.loadingDefaults.set(true);
    this.refs.getCurrent('tag:/' + this.ext()?.tag).pipe(
      catchError(err => {
        this.defaults.set({
          origin: this.store.account.origin(),
          url: 'tag:/' + this.ext()?.tag,
          tags: ['internal', this.store.account.localTag()],
          created: DateTime.now(),
          published: DateTime.now(),
          modified: DateTime.now(),
        });
        this.refs.create(this.defaults()!).subscribe(cursor => {
          this.defaults.update(defaults => defaults && { ...defaults, modifiedString: cursor });
        });
        return of(this.defaults());
      })
    ).subscribe(ref => {
      if (!this.loadingDefaults()) return;
      this.defaults.set(ref);
      this.loadingDefaults.set(false);
    });
  }
}

export function extForm(fb: UntypedFormBuilder, ext: Ext | undefined, admin: AdminService, locked: boolean) {
  let configControls = {};
  if (admin.getTemplate('') && !hasPrefix(ext?.tag, 'config')) {
    configControls = {
      ...configControls,
      header: [''],
      defaultSort: [[]],
      defaultFilter: [[]],
      sidebar: [''],
      popover: [''],
      modmail: [false],
      pinned: linksForm(fb, ext?.config?.pinned || []),
      themes: themesForm(fb, ext?.config?.themes || []),
      theme: [''],
    };
  }
  if (admin.home() && hasPrefix(ext?.tag, 'config/home')) {
    configControls = {
      ...configControls,
      header: [''],
      defaultSort: [[]],
      defaultFilter: [[]],
      sidebar: [''],
      modmail: [false],
      pinned: linksForm(fb, ext?.config?.pinned || []),
      themes: themesForm(fb, ext?.config?.themes || []),
      theme: [''],
    };
  }
  if (admin.getTemplate('user') && hasPrefix(ext?.tag, 'user')) {
    configControls = {
      ...configControls,
      userTheme: [''],
    };
  }
  return fb.group({
    tag: [{value: '', disabled: locked}, [Validators.required, Validators.pattern(TAG_REGEX)]],
    name: [''],
    config: fb.group(configControls),
  });
}
